import {
  getName,
  HOUR,
  isTeamId,
  isUserId,
  type TeamGetInput,
  type TeamGetSuccess,
  type TinyTeam,
  type TinyUser,
  type UserGetInput,
  type UserGetSuccess,
} from '@terros/common'
import { useQueries, type UseQueryResult } from '@tanstack/react-query'
import { tinyTeamKey, tinyUserKey, useServerRequest, useTeamCache, useUserCache } from '@front/model'
import { type CompareEntity, type CompareEntityId } from './compareUtil'

const toUserCompareEntity = (user: TinyUser): CompareEntity => ({
  kind: 'user',
  id: user.userId,
  name: getName(user),
})

const toTeamCompareEntity = (team: TinyTeam): CompareEntity => ({
  kind: 'team',
  id: team.teamId,
  name: team.name,
})

export const useSelectedEntityQueries = (entityIds: CompareEntityId[]): UseQueryResult<CompareEntity>[] => {
  const getUser = useServerRequest<UserGetInput, UserGetSuccess>('/user/get')
  const getTeam = useServerRequest<TeamGetInput, TeamGetSuccess>('/team/get')
  const userCache = useUserCache()
  const teamCache = useTeamCache()

  return useQueries({
    queries: entityIds.map((entityId) => {
      if (isUserId(entityId))
        return {
          queryKey: tinyUserKey(entityId),
          queryFn: async (): Promise<TinyUser> => {
            const { user } = await getUser({ userId: entityId })
            userCache.setUser(user)
            return user
          },
          select: toUserCompareEntity,
          gcTime: Infinity,
          staleTime: HOUR,
        }

      if (isTeamId(entityId))
        return {
          queryKey: tinyTeamKey(entityId),
          queryFn: async (): Promise<TinyTeam> => {
            const { team } = await getTeam({ teamId: entityId })
            teamCache.setTeam(team)
            return team
          },
          select: toTeamCompareEntity,
          gcTime: HOUR,
          staleTime: HOUR,
        }

      return {
        queryKey: ['permission-compare-role', entityId],
        queryFn: (): Promise<CompareEntity> => Promise.reject(new Error(`Unsupported entity: ${entityId}`)),
        enabled: false,
      }
    }),
  })
}
